import type { CapAutocompleteCity } from '../../../../components';
import { AcquisitionCustomerValues } from '../AcquisitionCustomerFields';
import { AcquisitionYesNo } from '../../../../services/api/acquisition/acquisitionOperation';
import {
  CreatePrestitoLeadCommand,
  PrestitoEmployeesNumber,
  PrestitoEmploymentContract,
  PrestitoLoanPurpose,
  PrestitoTenureBand,
} from '../../../../services/api/acquisition/prestitoAcquisition';
import { acquisitionAmountToNumber } from '../acquisitionAmount';
import {
  ACQUISITION_DEFAULT_PHONE_COUNTRY,
  acquisitionPhonePrefix,
} from '../AcquisitionPhonePrefixSelect';

/**
 * The Prestito draft an operator compiles. It is not the request body: amounts
 * are typed as grouped text, the telephone country only exists to pick a dial
 * code, and every conditional answer is `''` until its branch applies.
 * `buildPrestitoCommand` is the single place all of that becomes the flat,
 * closed command the backend accepts.
 */
export interface PrestitoFormValues extends AcquisitionCustomerValues {
  loanAmount: string;
  loanPurpose: PrestitoLoanPurpose | '';
  employmentContract: PrestitoEmploymentContract | '';
  yearsTaxNumber: PrestitoTenureBand | '';
  hasGuarantor: AcquisitionYesNo | '';
  yearsCurrentCompany: PrestitoTenureBand | '';
  monthlyIncome: string;
  monthlyIncomePensioner: string;
  hasOtherFinancing: AcquisitionYesNo | '';
  hasLatePreviousLoan: AcquisitionYesNo | '';
  employeesNumber: PrestitoEmployeesNumber | '';
  location: CapAutocompleteCity | null;
}

export const PRESTITO_DEFAULT_VALUES: PrestitoFormValues = {
  loanAmount: '',
  loanPurpose: '',
  employmentContract: '',
  yearsTaxNumber: '',
  hasGuarantor: '',
  yearsCurrentCompany: '',
  monthlyIncome: '',
  monthlyIncomePensioner: '',
  hasOtherFinancing: '',
  hasLatePreviousLoan: '',
  employeesNumber: '',
  location: null,
  firstName: '',
  lastName: '',
  email: '',
  phoneCountry: ACQUISITION_DEFAULT_PHONE_COUNTRY,
  phone: '',
};

/** The answers on which every conditional Prestito question depends. */
type PrestitoBranchAnswers = Pick<
  PrestitoFormValues,
  | 'employmentContract'
  | 'yearsTaxNumber'
  | 'hasOtherFinancing'
  | 'hasLatePreviousLoan'
>;

/** The fields whose question exists only for some customers. */
export type PrestitoConditionalField =
  | 'yearsTaxNumber'
  | 'hasGuarantor'
  | 'yearsCurrentCompany'
  | 'monthlyIncome'
  | 'monthlyIncomePensioner'
  | 'hasLatePreviousLoan'
  | 'employeesNumber';

const isEmployee = (contract: PrestitoFormValues['employmentContract']) =>
  contract === 'Dipendente tempo indeterminato' ||
  contract === 'Dipendente tempo determinato';

/**
 * Which questions the customer's own answers make applicable. This is the one
 * definition of the form's shape: the panel renders by it, clears by it, and
 * the command is compiled by it, so a field can never be hidden yet sent, or
 * asked yet dropped.
 */
export const PRESTITO_ASKS: Record<
  PrestitoConditionalField,
  (answers: PrestitoBranchAnswers) => boolean
> = {
  yearsTaxNumber: ({ employmentContract }) =>
    employmentContract === 'Lavoratore Autonomo/P.iva',
  hasGuarantor: ({ employmentContract, yearsTaxNumber }) =>
    employmentContract === 'Lavoratore Autonomo/P.iva' &&
    yearsTaxNumber === '1 anno o meno',
  yearsCurrentCompany: ({ employmentContract }) => isEmployee(employmentContract),
  // Everyone states an income; only its concept changes, so the ordinary field
  // stays visible until the operator says the customer is retired.
  monthlyIncome: ({ employmentContract }) => employmentContract !== 'Pensionato',
  monthlyIncomePensioner: ({ employmentContract }) =>
    employmentContract === 'Pensionato',
  hasLatePreviousLoan: ({ hasOtherFinancing }) => hasOtherFinancing === 'si',
  employeesNumber: ({ employmentContract, hasOtherFinancing, hasLatePreviousLoan }) =>
    employmentContract === 'Dipendente tempo indeterminato' &&
    hasOtherFinancing === 'si' &&
    hasLatePreviousLoan === 'si',
};

const PRESTITO_CONDITIONAL_FIELDS = Object.keys(
  PRESTITO_ASKS
) as PrestitoConditionalField[];

/** The conditional questions the current answers do not ask. */
export const prestitoInactiveFields = (
  answers: PrestitoBranchAnswers
): PrestitoConditionalField[] =>
  PRESTITO_CONDITIONAL_FIELDS.filter((field) => !PRESTITO_ASKS[field](answers));

/**
 * Compiles the draft into the command the backend will judge. A field whose
 * branch does not apply is omitted rather than sent empty: the schema is
 * closed, so an inactive value is a `400 invalid_request` instead of something
 * the backend quietly ignores. Unregistering an inactive field leaves it
 * absent from the submitted values, so the draft is completed from the
 * defaults before it is read.
 */
export const buildPrestitoCommand = (
  values: PrestitoFormValues
): CreatePrestitoLeadCommand => {
  const data = { ...PRESTITO_DEFAULT_VALUES, ...values };
  const asks = (field: PrestitoConditionalField) => PRESTITO_ASKS[field](data);

  return {
    firstName: data.firstName.trim(),
    lastName: data.lastName.trim(),
    email: data.email.trim(),
    phonePrefix: acquisitionPhonePrefix(data.phoneCountry),
    phone: data.phone.trim(),
    location: {
      comune: data.location?.nome ?? '',
      cap: data.location?.cap ?? '',
      provincia: data.location?.provincia ?? '',
      regione: data.location?.regione ?? '',
    },
    loanAmount: acquisitionAmountToNumber(data.loanAmount) as number,
    employmentContract:
      data.employmentContract as CreatePrestitoLeadCommand['employmentContract'],
    ...(asks('yearsTaxNumber')
      ? {
          yearsTaxNumber:
            data.yearsTaxNumber as CreatePrestitoLeadCommand['yearsTaxNumber'],
        }
      : {}),
    ...(asks('hasGuarantor')
      ? {
          hasGuarantor:
            data.hasGuarantor as CreatePrestitoLeadCommand['hasGuarantor'],
        }
      : {}),
    ...(asks('yearsCurrentCompany')
      ? {
          yearsCurrentCompany:
            data.yearsCurrentCompany as CreatePrestitoLeadCommand['yearsCurrentCompany'],
        }
      : {}),
    ...(asks('monthlyIncome')
      ? { monthlyIncome: acquisitionAmountToNumber(data.monthlyIncome) as number }
      : {}),
    ...(asks('monthlyIncomePensioner')
      ? {
          monthlyIncomePensioner: acquisitionAmountToNumber(
            data.monthlyIncomePensioner
          ) as number,
        }
      : {}),
    loanPurpose: data.loanPurpose as CreatePrestitoLeadCommand['loanPurpose'],
    hasOtherFinancing:
      data.hasOtherFinancing as CreatePrestitoLeadCommand['hasOtherFinancing'],
    ...(asks('hasLatePreviousLoan')
      ? {
          hasLatePreviousLoan:
            data.hasLatePreviousLoan as CreatePrestitoLeadCommand['hasLatePreviousLoan'],
        }
      : {}),
    ...(asks('employeesNumber')
      ? {
          employeesNumber:
            data.employeesNumber as CreatePrestitoLeadCommand['employeesNumber'],
        }
      : {}),
  };
};
